import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import SingleMaterial from '@features/order/components/SingleMaterial';

import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchBrandingCategory, fetchEnumFilter, fetchTrims } from '@store/design/action';
import { fetchOrderPlaceWorkflow } from '@store/orders/actions';
import { getEnumFilter } from '@store/reducers';

import { fetchSupplierByMaterialType } from '@services/supplierService';

import ApiClient from '@lib/apiClient';

import { fetchAllBriefMaterialWithoutRedux, fetchApplicationGarmentsTypeList } from '@utils/MaterialsUtil';

import { BASE_URL, MATERIAL_TYPE, MATERIALS_TYPE } from '@/constants/constant';

const MaterialListTab = () => {
	const [loading, setLoading] = useState(false);
	const [materialList, setMaterialList] = useState([]);
	const [designLists, setDesignLists] = useState([]);
	const [isAddNewItem, setIsAddNewItem] = useState(false);
	const [isEditItem, setIsEditItem] = useState(false);
	const [cellId, setCellId] = useState('');
	const [editCellId, setEditCellId] = useState('');
	const [errors, setErrors] = useState(null);
	const [designIdToView, setDesignIdToView] = useState(null);
	const [appGarments, setAppGarments] = useState([]);
	const [materialTypes, setMaterialTypes] = useState([]);
	const [supplierList, setSupplierList] = useState([]);
	const dispatch = useDispatch();
	let { id } = useParams();
	const allUnitTypes = useSelector(getEnumFilter).unitType;
	const [selectedDesign, setSelectedDesign] = useState(null);

	const onClickDesignImage = (designId) => {
		setDesignIdToView(designId);
	};

	const onCloseViewModal = () => {
		setDesignIdToView(null);
		dispatch(clearDesignViewModalInfo({}));
	};

	const getMaterialList = async (orderId) => {
		setLoading(true);
		await ApiClient.GET('getOrderMaterials', orderId)
			.then(({ data }) => {
				setLoading(false);
				setMaterialList({
					LIBRARY_FABRIC: data?.LIBRARY_FABRIC || [],
					BRANDING: data?.BRANDING || [],
					TRIMS: data?.TRIMS || []
				});
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const getOrderQuotes = async (orderId) => {
		setLoading(true);
		await ApiClient.GET(`${BASE_URL}/order/materials/${orderId}/designs`, '', true)
			.then(({ data }) => {
				setLoading(false);
				setDesignLists(
					data.map((item) => {
						return {
							...item,
							value: `${item?.productName} (${item?.productRefNumber})`,
							id: item?.productId
						};
					})
				);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const resetAll = () => {
		setIsAddNewItem(false);
		setIsEditItem(false);
		setCellId('');
		setErrors(null);
	};
	const onCancelEdit = () => {
		setIsEditItem(false);
		resetAll();
		getMaterialList(id);
	};

	const onSubmitMaterialAdd = async (body) => {
		await ApiClient.POST('addOrderMaterial', [body], id)
			.then(({ data }) => {
				setLoading(false);
				if (data && data.success) {
					getMaterialList(id);
					dispatch(fetchOrderPlaceWorkflow(id));
					resetAll();
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSubmitMaterialUpdate = async (body, addedMaterialId) => {
		await ApiClient.PUT('updateOrderMaterial', { ...body }, addedMaterialId)
			.then(({ data }) => {
				setLoading(false);
				if (data && data.success) {
					getMaterialList(id);
					dispatch(fetchOrderPlaceWorkflow(id));
					resetAll();
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSetMaterialType = (type, materialType) => {
		if (type === MATERIAL_TYPE.LIBRARY_FABRIC) return materialType;
		if (type === MATERIAL_TYPE.TRIMS) return MATERIAL_TYPE.TRIMS;
		if (type === MATERIAL_TYPE.BRANDING) return MATERIAL_TYPE.BRANDING;
	};

	const onAddMaterialList = async (addedMaterial, type) => {
		setLoading(true);
		const body = {
			productId: addedMaterial.productId,
			materialId: addedMaterial.materialId,
			supplierId: addedMaterial?.supplierId,
			materialType: onSetMaterialType(type, addedMaterial.materialType),
			productInfoForRfqId: addedMaterial.productInfoForRfqId,
			unitConsumption: addedMaterial.unitConsumption,
			isImported: addedMaterial.isImported || false,
			unitType: addedMaterial.unitType,
			duplicateFlag: addedMaterial.duplicateFlag ? addedMaterial.duplicateFlag : false,
			extraFabricType: addedMaterial.extraFabricType,
			placementIdList: addedMaterial.placementIdList
		};
		if (isAddNewItem) onSubmitMaterialAdd(body);
		if (isEditItem) onSubmitMaterialUpdate(body, addedMaterial.id);
	};

	useEffect(() => {
		setLoading(true);
		getMaterialList(id);
		getOrderQuotes(id);
		dispatch(fetchTrims()).finally(() => {
			setLoading(false);
		});
		dispatch(fetchBrandingCategory()).finally(() => {
			setLoading(false);
		});
		fetchApplicationGarmentsTypeList(setAppGarments).finally(setLoading(false));
		dispatch(fetchEnumFilter('info.nitex.app.materials.UnitType'));
		onFetchSupplierList();
	}, [id]);

	const getMaterialType = (supplierType = 'MAIN_FABRIC') => {
		let typeName = supplierType;
		if (supplierType === 'TRIMS_ACCESSORIES') {
			typeName = 'TRIMS';
		} else if (supplierType === 'BRANDING') {
			typeName = 'ACCESSORIES';
		}
		return typeName;
	};

	const onGetSupplierMaterials = (supplierType, categories) => {
		fetchAllBriefMaterialWithoutRedux(
			getMaterialType(supplierType),
			categories,
			setMaterialTypes
		).finally();
	};

	const onFetchSupplierList = async (value = '', supplierType) => {
		try {
			dispatch(openLoader());
			let response = await fetchSupplierByMaterialType(value, getMaterialType(supplierType));
			response = response?.data?.map((item) => {
				return {
					id: item.id,
					value: item.name
				};
			});
			setSupplierList(response);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onPopulateDynamicMaterials = (type, materialItem, searchItem = '') => {
		if (type === MATERIAL_TYPE.TRIMS) {
			let categories = { trimsCategories: materialItem.trimsCategory, search: searchItem };
			onGetSupplierMaterials(MATERIALS_TYPE.TRIMS_ACCESSORIES, categories);
		}
		if (type === MATERIAL_TYPE.BRANDING) {
			let categories = {
				brandingCategories: materialItem.brandingCategory,
				search: searchItem
			};
			onGetSupplierMaterials(MATERIALS_TYPE.BRANDING, categories);
		}
		if (type === MATERIAL_TYPE.LIBRARY_FABRIC) {
			let categories = { search: searchItem };
			onGetSupplierMaterials(MATERIALS_TYPE.FABRIC, categories);
		}
	};

	const onEditClick = (type, idx) => {
		setIsEditItem(true);
		setEditCellId(`${idx}-editItem-${type}`);
	};

	const onAddNewItem = (materialType) => {
		let otherItem = {
			materialId: null,
			materialType: '',
			unitConsumption: null,
			isImported: false,
			productInfoForRfqId: null,
			unitType: ''
		};
		const newItem = {
			...materialList,
			[materialType]: [...materialList[materialType], otherItem]
		};
		setMaterialList(newItem);
		const index = newItem[materialType].length;
		setCellId(`${index - 1}-newItem-${materialType}`);
		setIsAddNewItem(true);
	};

	const onDuplicateClick = (materialType, idx, id) => {
		const parentItem = materialList[materialType].find((item) => item.id === id);
		let otherItem = {
			materialId: parentItem?.materialId,
			materialName: parentItem?.materialName,
			supplierId: parentItem?.supplierId,
			supplierName: parentItem?.supplierName,
			unitConsumption: parentItem?.unitConsumption,
			productInfoForRfqId: null,
			quantity: parentItem?.quantity,
			unitType: parentItem?.unitType,
			isImported: parentItem.isImported || false,
			duplicateFlag: true
		};
		if (materialType === MATERIAL_TYPE.LIBRARY_FABRIC) {
			otherItem.materialType = parentItem?.materialType;
		} else if (materialType === MATERIAL_TYPE.TRIMS) {
			otherItem.trimsCategory = parentItem?.trimsCategory;
		} else {
			otherItem.brandingCategory = parentItem?.brandingCategory;
		}
		const newItem = {
			...materialList,
			[materialType]: [...materialList[materialType], otherItem]
		};
		setMaterialList(newItem);
		const index = newItem[materialType].length;
		setCellId(`${index - 1}-newItem-${materialType}`);
		setIsAddNewItem(true);
	};

	const onChangeMaterial = (
		type,
		name,
		value,
		productName,
		id,
		quantity,
		productId,
		idx,
		isSetMember,
		nameOfSet,
		selectedValue
	) => {
		const newData = { ...materialList };
		const materialItem = newData[type];
		materialItem[idx][name] = value;
		materialItem[idx]['productName'] = productName;
		materialItem[idx]['productInfoForRfqId'] = id;
		materialItem[idx]['quantity'] = quantity;
		materialItem[idx]['productId'] = productId;
		materialItem[idx]['isSetMember'] = isSetMember;
		materialItem[idx]['setName'] = nameOfSet;
		newData[idx] = materialItem;
		setMaterialList(newData);
		setSelectedDesign({
			id,
			value: selectedValue
		});
	};

	const onMaterialCategoryAndUnitChange = (type, value, name, idx) => {
		const newData = { ...materialList };
		const materialItem = newData[type];
		if (name === 'unitConsumption') {
			materialItem[idx][name] = parseFloat(value);
		} else if (name === 'placement') {
			materialItem[idx]['extraFabricType'] = value?.value;
			if (MATERIAL_TYPE.LIBRARY_FABRIC === type)
				materialItem[idx]['materialType'] = value?.materialType;
			else materialItem[idx]['materialType'] = type;
			onPopulateDynamicMaterials(type, materialItem[idx]);
		} else if (name === 'isImported') {
			let updatedValue = value === 'Yes';
			materialItem[idx]['isImported'] = updatedValue;
		} else {
			materialItem[idx][name] = value;
		}
		newData[idx] = materialItem;
		setMaterialList(newData);
	};

	const onUpdatePlacement = (placement = [], idx, type) => {
		const newData = { ...materialList };
		const materialItem = newData[type];
		materialItem[idx]['placementIdList'] = placement;
		materialItem[idx]['placementResponseList'] = appGarments.filter((item) =>
			placement.includes(item.id)
		);

		newData[idx] = materialItem;
		setMaterialList(newData);
	};

	const onClickMaterial = (idx, type, item, dataType) => {
		const newData = { ...materialList };
		const materialItem = newData[type];
		materialItem[idx][dataType === 'MATERIAL' ? 'materialId' : 'supplierId'] = item?.id;
		materialItem[idx][dataType === 'MATERIAL' ? 'materialName' : 'supplierName'] = item?.value;
		newData[idx] = materialItem;
		setMaterialList(newData);
	};
	const validateMaterial = (item) => {
		let materialErrors = {};
		let formIsValid = true;
		if (!item.productId) {
			formIsValid = false;
			materialErrors['designError'] = true;
		}
		if (!item?.placementIdList?.length) {
			formIsValid = false;
			materialErrors['type'] = true;
		}
		if (!item.materialId) {
			formIsValid = false;
			materialErrors['materialError'] = true;
		}
		if (!item.supplierId) {
			formIsValid = false;
			materialErrors['supplierError'] = true;
		}
		if (!item.unitConsumption) {
			formIsValid = false;
			materialErrors['unitConsumptionError'] = true;
		}
		if (!item.unitType) {
			formIsValid = false;
			materialErrors['unitTypeTypeError'] = true;
		}
		setErrors({ ...materialErrors });
		return formIsValid;
	};

	const onAddNew = (idx, type) => {
		const newData = { ...materialList };
		const materialItem = newData[type];
		const addedMaterial = {
			...materialItem[idx],
			placementIdList: materialItem[idx].placementResponseList.map((item) => item.id)
		};
		let isValidMaterial = validateMaterial(addedMaterial);
		if (!isValidMaterial) return;
		onAddMaterialList(addedMaterial, type);
	};

	const onRemoveItem = async (type, item, idx) => {
		setLoading(true);
		if (!item?.id) {
			setLoading(false);
			const newData = { ...materialList };
			const materialItem = newData[type];
			const updatedItems = materialItem.splice(idx, 1);
			newData[idx] = updatedItems;
			setMaterialList(newData);
			resetAll();
			return;
		}
		await ApiClient.DELETE('removeOrderMaterial', {}, item.id)
			.then(({ data }) => {
				setLoading(false);
				if (data && data.success) {
					getMaterialList(id);
					toastSuccess(data.message);
					resetAll();
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				resetAll();
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	useEffect(() => {
		if (designIdToView !== null) {
			openDesignViewModal();
		}
	}, [designIdToView]);

	const openDesignViewModal = () => {
		dispatch(
			setDesignViewModalInfo({
				id: designIdToView,
				onCloseViewModal: onCloseViewModal,
				source: 'ORDER_DETAILS',
				orderId: id
			})
		);
	};

	const renderMaterialCardListTable = (thTypeName, list, MaterialType) => {
		return (
			<div className="card-body">
				<div className="update-material-list-table">
					<table className="table">
						<thead>
							<tr>
								<th>Designs</th>
								<th>{thTypeName}</th>
								<th>Material</th>
								<th>Imported</th>
								<th>Supplier</th>
								<th>Unit consumption</th>
								<th>Quantity</th>
								<th>Total consumption</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{list?.map((item, idx) => (
								<React.Fragment key={item.id}>
									<SingleMaterial
										errors={errors}
										idx={idx}
										cellId={cellId}
										materialItem={item}
										isAddNewItem={isAddNewItem}
										type={MaterialType}
										designLists={designLists}
										onChangeMaterial={onChangeMaterial}
										onClickMaterial={onClickMaterial}
										materialsData={materialTypes}
										allUnitTypes={allUnitTypes}
										onAddNew={onAddNew}
										onEditClick={onEditClick}
										isEditItem={isEditItem}
										editCellId={editCellId}
										onRemoveItem={onRemoveItem}
										onCancelEdit={onCancelEdit}
										onPopulateDynamicMaterials={onPopulateDynamicMaterials}
										onMaterialCategoryAndUnitChange={onMaterialCategoryAndUnitChange}
										onDuplicateClick={onDuplicateClick}
										onClickDesignImage={onClickDesignImage}
										appGarments={appGarments}
										setAppGarments={setAppGarments}
										setLoading={setLoading}
										supplierList={supplierList}
										selectedDesign={selectedDesign}
										onUpdatePlacement={onUpdatePlacement}
									/>
								</React.Fragment>
							))}
							<tr>
								<td colSpan={9}>
									<button
										disabled={cellId}
										className="button text pl-0"
										onClick={() => onAddNewItem(MaterialType)}
									>
										<img
											src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
											alt={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
										/>
										<span className="regular-14">Item</span>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	return (
		<LoaderComponent loading={loading}>
			<div className="create-order-container">
				<div className="filter-bar-section material-list-toggle-section">
					<div className="filter-content">
						<div id="accordion">
							<div className="card">
								<div className="card-header" id="headingStyleInfo">
									<h5 className="mb-0">
										<button
											className="btn btn-link"
											data-toggle="collapse"
											data-target="#collapseStyleInfo"
											aria-expanded="true"
											aria-controls="collapseStyleInfo"
											onClick={() => {
												resetAll();
											}}
										>
											FABRIC
										</button>
									</h5>
								</div>
								<div
									id="collapseStyleInfo"
									className="collapse show"
									aria-labelledby="headingStyleInfo"
									// data-parent="#accordion"
								>
									{renderMaterialCardListTable(
										'Placement',
										materialList?.LIBRARY_FABRIC,
										MATERIAL_TYPE.LIBRARY_FABRIC
									)}
								</div>
							</div>
							<div className="card">
								<div className="card-header" id="TrimsAccessories">
									<h5 className="mb-0">
										<button
											className="btn btn-link collapsed"
											data-toggle="collapse"
											data-target="#collapseTrimsAccessories"
											aria-expanded="true"
											aria-controls="collapseTrimsAccessories"
											onClick={() => {
												resetAll();
											}}
										>
											TRIMS
										</button>
									</h5>
								</div>
								<div
									id="collapseTrimsAccessories"
									className="collapse"
									aria-labelledby="TrimsAccessories"
									// data-parent="#accordion"
								>
									{renderMaterialCardListTable(
										'Material Type & Placement',
										materialList?.TRIMS,
										MATERIAL_TYPE.TRIMS
									)}
								</div>
							</div>
							<div className="card">
								<div className="card-header" id="LabelPackaging">
									<h5 className="mb-0">
										<button
											className="btn btn-link collapsed"
											data-toggle="collapse"
											data-target="#collapseLabelPackaging"
											aria-expanded="true"
											aria-controls="collapseLabelPackaging"
											onClick={() => resetAll()}
										>
											ACCESSORIES
										</button>
									</h5>
								</div>
								<div
									id="collapseLabelPackaging"
									className="collapse"
									aria-labelledby="LabelPackaging"
								>
									{renderMaterialCardListTable(
										'Material Type & Placement',
										materialList?.BRANDING,
										MATERIAL_TYPE.BRANDING
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default MaterialListTab;
